import DesignTokens from '@tableau/design-tokens';
import { CopyPasteIcon } from '@tableau/icons/CopyPasteIcon';
import { DownloadBaseIcon } from '@tableau/icons/DownloadBaseIcon';
import { ButtonType, ButtonWidget, DialogFooterButtonsWidget, DialogProps, DialogWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

import { SettingsStyles } from '../../../styles/CommonStyles';
import { IDownloadScriptParams } from '../../serverApi/requests/DownloadScript';
import { BaasEdgePoolAndCluster } from '../../serverApi/resources/BaasEdgePoolAndCluster';


export type AddNodeDialogProps = Readonly<{
  descriptionPart1: string;
  descriptionPart2: string;
  descriptionPart3: string;
  cammandLine1: string;
  cammandLine2: string;
  cammandLine3: string;
  cammandLine4: string;
  cammandLine5: string;
  dismiss: (reason?: any) => void;
}>

export type AddNodeDialogState = Readonly<{
  hasError: boolean;
}>

const downloadButtonStyle: React.CSSProperties = {
  paddingRight: 14,
  paddingLeft: 14
};

export function GetAddNodeFooterContent(props: AddNodeDialogProps, clusterId: string) {
  const downloadAction = (_: AddNodeDialogProps) => {
    //const downloadUrl='/v1/baas/'+pool.clusterId+'/onboardNodeFile';
    const downloadScriptParam: IDownloadScriptParams = {
      clusterId: clusterId
    };

    BaasEdgePoolAndCluster.downloadScript(downloadScriptParam).finally(
      () =>{
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsClusterAddNodeDescriptionDownloadSuccess() });
        _.dismiss();
      }
    );
  };

  return (
    <>
    <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: () => {
            props.dismiss();
          },
          buttonType: ButtonType.Outline,
          children: Messages.close(),
          testId: 'add-node-footer-close',
        },
        {
          handleClick: () => {
            downloadAction(props);
          },
          buttonType: ButtonType.Primary,
          children: <>
            <DownloadBaseIcon size={14} alt={'download paste button'} style={{verticalAlign: 'middle', paddingRight: 3}}/>
            {Messages.dataConnectSiteSettingsClusterAddNode()}
          </>,
          style: downloadButtonStyle,
          testId: 'add-node-footer-download',
        }
      ]}
      testId='add-node-footer'
    />
    </>
  );
}


export function GetAddNodeDialogWidget(dialogProps: DialogProps, props: AddNodeDialogProps) {
  return (
    <DialogWidget {...dialogProps} dialogWidth={TabDialogWidth.width375px}>
      {AddNodeDialogBody(props)}
    </DialogWidget>
  );
}

export function AddNodeDialogBody(props: AddNodeDialogProps) {

  const descriptionDivClassName = `${SettingsStyles.settingsGroupDescription}`;
  const copyCode = ()=>{
    const code = document.getElementById('add-node-dialog-code-block').innerText;
    navigator.clipboard.writeText(code);
    ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsClusterAddNodeScriptCopied() });
  };
  return (
    <div className={fieldClass}>
      <div className={descriptionDivClassName} data-tb-test-id={'add-node-dialog-details'}>
                <span className={descriptionLineClass} style={{marginTop: 0}}>
                  {props.descriptionPart1}
                </span>
                <span className={descriptionLineClass}>
                  {props.descriptionPart2}
                </span>
                <span className={descriptionLineClass}>
                  {props.descriptionPart3}
                </span>
                <div className={container} >
                  <div className={codeContainer}>
                    <code id={'add-node-dialog-code-block'} >
                      <span className={cammandLineClass}>
                        {props.cammandLine1}
                      </span>
                      <span className={cammandLineClass}>
                        {props.cammandLine2}
                      </span>
                      <span className={cammandLineClass}>
                        {props.cammandLine3}
                      </span>
                      <span className={cammandLineClass}>
                        {props.cammandLine4}
                      </span>
                      <span className={cammandLineClass}>
                        {props.cammandLine5}
                      </span>
                    </code>
                  </div>
                  <div className={buttonContainer}>
                      <ButtonWidget
                          testId={'copy-code-btn'} buttonType={ButtonType.Outline}
                          tabIndex={-1}
                          onClick={copyCode}
                          className={copyBtn}
                        >
                          <CopyPasteIcon size={16} alt={'copy paste button'} className={copyBtnIconClass}/>
                      </ButtonWidget>
                  </div>
                </div>
      </div>
    </div>
  );
}

const fieldClass = style({ marginBottom: TabStyles.Sizing.BaseUnit * 5 });
const descriptionLineClass = style({ marginTop: 8, display: 'block' });
const cammandLineClass = style({ marginTop: 10, display: 'block' });


export const errorClass = style({ marginTop: TabStyles.Spacing.DefaultSpace, color: applyColor(DesignTokens.color.font.default.error, TabStyles.Colors.AttentionDark) });
const container = style({
  display: 'flex',
  marginTop: 10,
  backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.active, CommonStyles.Colors.Grey230),
  boxShadow: '0 4px 8px '+ applyColor(DesignTokens.color.elevation.surface.default.default, 'rgba(0, 0, 0, 0.1)'),
  position: 'relative',
});

const codeContainer = style({
  verticalAlign: 'top',
  textAlign: 'left',
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: 16,
 });

const buttonContainer = style({
  width: 30,
  verticalAlign: 'top',
  position: 'relative',

 });

 const copyBtn = style({
  padding: 0,
  position: 'absolute',
  top: 8,
  right: 8,
  borderWidth: 1,
  borderRadius: 3,
  borderColor: applyColor(DesignTokens.color.component.border.neutral.default.default, CommonStyles.Colors.Grey40),
  transition:'background-color 0.3s',
  minWidth: 30,
  minHeight: 24
 });

 const copyBtnIconClass = cssClass('copy-code', 'icon', {
  verticalAlign: 'middle',
});
